import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BadgeAlert, CheckCircle2, Eye, Plus, Search, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';

function money(value, currency = 'UGX') {
  return `${currency} ${Number(value || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
}

function formatDate(value) {
  if (!value) return '-';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}

function statusTone(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'approved') return 'bg-emerald-100 text-emerald-800';
  if (s === 'rejected') return 'bg-red-100 text-red-800';
  return 'bg-amber-100 text-amber-800';
}

function currentMonth() {
  return String(new Date().getMonth() + 1);
}

function currentYear() {
  return String(new Date().getFullYear());
}

export default function BudgetExpenseRequests() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [filters, setFilters] = useState({
    status: 'all',
    branch_id: 'all',
    month: currentMonth(),
    year: currentYear(),
    search: '',
  });
  const [createOpen, setCreateOpen] = useState(false);
  const [viewItem, setViewItem] = useState(null);
  const [rejectItem, setRejectItem] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [form, setForm] = useState({
    branch_id: 'all',
    category_id: '',
    amount: '',
    description: '',
    attachment_url: '',
    date: new Date().toISOString().slice(0, 16),
  });

  const load = async (nextFilters = filters) => {
    setLoading(true);
    try {
      const params = {
        status: nextFilters.status || undefined,
        branch_id: nextFilters.branch_id && nextFilters.branch_id !== 'all' ? nextFilters.branch_id : undefined,
        month: nextFilters.month || undefined,
        year: nextFilters.year || undefined,
      };
      const res = await api.get('/budget/expense-requests', { params });
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || 'Failed to load expense requests');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const branches = useMemo(() => Array.isArray(data?.branches) ? data.branches : [], [data?.branches]);
  const categories = useMemo(() => Array.isArray(data?.categories) ? data.categories : [], [data?.categories]);
  const activeBranches = useMemo(() => branches.filter((branch) => String(branch?.status || 'active').toLowerCase() === 'active'), [branches]);
  const requests = Array.isArray(data?.requests) ? data.requests : [];
  const summary = data?.summary || {};
  const canApprove = !!data?.can_approve;
  const singleBranch = activeBranches.length <= 1;
  const defaultCurrency = user?.default_currency || 'UGX';

  const refresh = async () => load(filters);

  const updateFilter = (key, value) => {
    const next = { ...filters, [key]: value };
    setFilters(next);
    load(next);
  };

  const resetForm = () => {
    setForm({
      branch_id: singleBranch ? (activeBranches[0]?.id || branches[0]?.id || '') : (activeBranches[0]?.id || branches[0]?.id || ''),
      category_id: categories[0]?.id || '',
      amount: '',
      description: '',
      attachment_url: '',
      date: new Date().toISOString().slice(0, 16),
    });
  };

  const openCreate = () => {
    resetForm();
    setCreateOpen(true);
  };

  const saveRequest = async () => {
    if (!form.category_id) return toast.error('Please choose a category.');
    if (!singleBranch && !form.branch_id) return toast.error('Please choose a branch.');
    if (!Number(form.amount || 0)) return toast.error('Amount must be greater than zero.');
    setSaving(true);
    try {
      const payload = {
        branch_id: singleBranch ? (activeBranches[0]?.id || branches[0]?.id || form.branch_id) : form.branch_id,
        category_id: form.category_id,
        amount: Number(form.amount || 0),
        description: form.description,
        attachment_url: form.attachment_url,
        date: form.date,
        currency: defaultCurrency,
      };
      await api.post('/budget/expense-requests', payload);
      toast.success('Expense request submitted');
      setCreateOpen(false);
      resetForm();
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || 'Failed to submit request');
    } finally {
      setSaving(false);
    }
  };

  const approveRequest = async (request) => {
    if (!canApprove) return toast.error('You do not have permission to approve expense requests.');
    setApproving(true);
    try {
      const res = await api.post(`/budget/expense-requests/${request.id}/approve`, { create_expense_transaction: true });
      if (res.data?.created_expense_transaction) {
        toast.success('Request approved and expense recorded');
      } else {
        toast.success('Request approved');
      }
      await refresh();
      setViewItem((current) => current && current.id === request.id ? { ...current, status: 'approved' } : current);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || 'Failed to approve request');
    } finally {
      setApproving(false);
    }
  };

  const openReject = (request) => {
    setRejectItem(request);
    setRejectionReason('');
  };

  const rejectRequest = async () => {
    if (!rejectItem) return;
    if (!rejectionReason.trim()) return toast.error('Please provide a rejection reason.');
    setRejecting(true);
    try {
      await api.post(`/budget/expense-requests/${rejectItem.id}/reject`, { rejection_reason: rejectionReason });
      toast.success('Request rejected');
      setRejectItem(null);
      setRejectionReason('');
      await refresh();
      setViewItem((current) => current && current.id === rejectItem.id ? { ...current, status: 'rejected' } : current);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || 'Failed to reject request');
    } finally {
      setRejecting(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-6 h-6 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (data?.upgrade_required) {
    return (
      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardContent className="p-8 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-emerald-50 flex items-center justify-center">
              <BadgeAlert size={22} className="text-emerald-700" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Expense Requests</h1>
              <p className="text-sm text-slate-500">Upgrade your package to unlock Budget and Financial Intelligence.</p>
            </div>
          </div>
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-amber-900 text-sm">
            {data?.message || 'This feature is available on premium packages only.'}
          </div>
          <div className="flex gap-2">
            <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => navigate('/dashboard/package')}>
              View Packages
            </Button>
            <Button variant="outline" className="border-slate-200" onClick={refresh}>
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Expense Requests
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Submit, review, approve, and reject expense requests.</p>
        </div>
        <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={openCreate}>
          <Plus size={16} className="mr-1.5" /> New Request
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardContent className="p-5">
            <p className="text-sm font-medium text-slate-500">Pending Requests</p>
            <p className="text-2xl font-bold text-slate-900 mono-nums mt-2">{summary.pending_requests || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardContent className="p-5">
            <p className="text-sm font-medium text-slate-500">Approved This Month</p>
            <p className="text-2xl font-bold text-slate-900 mono-nums mt-2">{summary.approved_this_month || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardContent className="p-5">
            <p className="text-sm font-medium text-slate-500">Rejected This Month</p>
            <p className="text-2xl font-bold text-slate-900 mono-nums mt-2">{summary.rejected_this_month || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
          <CardContent className="p-5">
            <p className="text-sm font-medium text-slate-500">Total Approved Amount</p>
            <p className="text-2xl font-bold text-slate-900 mono-nums mt-2">{money(summary.total_approved_amount || 0, defaultCurrency)}</p>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader className="space-y-3">
          <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Filters</CardTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
            <Select value={filters.status} onValueChange={(value) => updateFilter('status', value)}>
              <SelectTrigger className="bg-white border-slate-200"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.branch_id} onValueChange={(value) => updateFilter('branch_id', value)}>
              <SelectTrigger className="bg-white border-slate-200"><SelectValue placeholder="Branch" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Branches</SelectItem>
                {branches.map((branch) => (
                  <SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input type="number" min="1" max="12" value={filters.month} onChange={(e) => updateFilter('month', e.target.value)} placeholder="Month" className="bg-white" />
            <Input type="number" min="2020" max="2100" value={filters.year} onChange={(e) => updateFilter('year', e.target.value)} placeholder="Year" className="bg-white" />
            <div className="relative xl:col-span-1 md:col-span-2">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={filters.search}
                onChange={(e) => updateFilter('search', e.target.value)}
                placeholder="Search requests"
                className="pl-9 bg-white"
              />
            </div>
          </div>
        </CardHeader>
      </Card>

      {data?.can_approve === false ? (
        <Card className="border border-amber-200 bg-amber-50 rounded-2xl">
          <CardContent className="p-4 text-sm text-amber-900">
            You can submit expense requests, but only the owner or admin can approve or reject them.
          </CardContent>
        </Card>
      ) : null}

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader className="flex flex-row items-center justify-between gap-3">
          <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Requests</CardTitle>
          <Badge variant="outline" className="border-slate-200 text-slate-600 bg-slate-50">{requests.length} item(s)</Badge>
        </CardHeader>
        <CardContent>
          {requests.length ? (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-500 border-b border-slate-100">
                    <th className="py-3 pr-4 font-medium">Submitted By</th>
                    <th className="py-3 pr-4 font-medium">Branch</th>
                    <th className="py-3 pr-4 font-medium">Category</th>
                    <th className="py-3 pr-4 font-medium">Amount</th>
                    <th className="py-3 pr-4 font-medium">Date</th>
                    <th className="py-3 pr-4 font-medium">Status</th>
                    <th className="py-3 pr-4 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map((request) => {
                    const branch = branches.find((item) => item.id === request.branch_id);
                    const category = categories.find((item) => item.id === request.category_id);
                    return (
                      <tr key={request.id} className="border-b border-slate-50 last:border-0">
                        <td className="py-3 pr-4 text-slate-700">{request.submitted_by_name || request.submitted_by || '-'}</td>
                        <td className="py-3 pr-4 text-slate-700">{branch?.name || 'Main Branch'}</td>
                        <td className="py-3 pr-4 text-slate-700">{category?.name || 'Uncategorized'}</td>
                        <td className="py-3 pr-4 font-medium text-slate-900">{money(request.amount, request.currency || defaultCurrency)}</td>
                        <td className="py-3 pr-4 text-slate-700">{formatDate(request.created_at)}</td>
                        <td className="py-3 pr-4">
                          <Badge className={statusTone(request.status)}>{request.status || '-'}</Badge>
                        </td>
                        <td className="py-3 pr-4">
                          <div className="flex flex-wrap gap-2">
                            <Button size="sm" variant="outline" className="border-slate-200" onClick={() => setViewItem(request)}>
                              <Eye size={14} className="mr-1" /> View
                            </Button>
                            {canApprove && String(request.status || '').toLowerCase() === 'pending' ? (
                              <>
                                <Button size="sm" className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => approveRequest(request)} disabled={approving}>
                                  <CheckCircle2 size={14} className="mr-1" /> Approve
                                </Button>
                                <Button size="sm" variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" onClick={() => openReject(request)} disabled={rejecting}>
                                  <XCircle size={14} className="mr-1" /> Reject
                                </Button>
                              </>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-slate-400 text-center py-8">No expense requests found.</p>
          )}
        </CardContent>
      </Card>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>New Expense Request</DialogTitle>
            <DialogDescription>Submit a request for approval. The owner or admin can review it later.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            {!singleBranch ? (
              <div className="grid gap-2">
                <Label>Branch</Label>
                <Select value={form.branch_id} onValueChange={(value) => setForm((prev) => ({ ...prev, branch_id: value }))}>
                  <SelectTrigger><SelectValue placeholder="Select branch" /></SelectTrigger>
                  <SelectContent>
                    {activeBranches.map((branch) => <SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>)}
                    {!activeBranches.length && branches.map((branch) => <SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ) : null}
            <div className="grid gap-2">
              <Label>Category</Label>
              <Select value={form.category_id} onValueChange={(value) => setForm((prev) => ({ ...prev, category_id: value }))}>
                <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {categories.map((category) => <SelectItem key={category.id} value={category.id}>{category.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label>Amount</Label>
                <Input type="number" value={form.amount} onChange={(e) => setForm((prev) => ({ ...prev, amount: e.target.value }))} />
              </div>
              <div className="grid gap-2">
                <Label>Date</Label>
                <Input type="datetime-local" value={form.date} onChange={(e) => setForm((prev) => ({ ...prev, date: e.target.value }))} />
              </div>
            </div>
            <div className="grid gap-2">
              <Label>Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))} placeholder="What is the expense for?" />
            </div>
            <div className="grid gap-2">
              <Label>Attachment URL (optional)</Label>
              <Input value={form.attachment_url} onChange={(e) => setForm((prev) => ({ ...prev, attachment_url: e.target.value }))} placeholder="Paste a document or receipt link" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-slate-200" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={saveRequest} disabled={saving}>
              {saving ? 'Submitting...' : 'Submit Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewItem} onOpenChange={(open) => !open && setViewItem(null)}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Expense Request Details</DialogTitle>
            <DialogDescription>Review the request before approval or rejection.</DialogDescription>
          </DialogHeader>
          {viewItem ? (
            <div className="grid gap-3 text-sm py-2">
              <div className="grid grid-cols-2 gap-3">
                <div><p className="text-slate-500">Submitted By</p><p className="font-medium text-slate-900">{viewItem.submitted_by_name || viewItem.submitted_by || '-'}</p></div>
                <div><p className="text-slate-500">Status</p><Badge className={statusTone(viewItem.status)}>{viewItem.status || '-'}</Badge></div>
                <div><p className="text-slate-500">Branch</p><p className="font-medium text-slate-900">{branches.find((b) => b.id === viewItem.branch_id)?.name || 'Main Branch'}</p></div>
                <div><p className="text-slate-500">Category</p><p className="font-medium text-slate-900">{categories.find((c) => c.id === viewItem.category_id)?.name || 'Uncategorized'}</p></div>
                <div><p className="text-slate-500">Amount</p><p className="font-medium text-slate-900">{money(viewItem.amount, viewItem.currency || defaultCurrency)}</p></div>
                <div><p className="text-slate-500">Date</p><p className="font-medium text-slate-900">{formatDate(viewItem.created_at)}</p></div>
              </div>
              <div>
                <p className="text-slate-500 mb-1">Description</p>
                <p className="text-slate-900">{viewItem.description || '-'}</p>
              </div>
              {viewItem.attachment_url ? (
                <div>
                  <p className="text-slate-500 mb-1">Attachment</p>
                  <a href={viewItem.attachment_url} target="_blank" rel="noreferrer" className="text-emerald-700 underline break-all">
                    {viewItem.attachment_url}
                  </a>
                </div>
              ) : null}
              {viewItem.rejection_reason ? (
                <div>
                  <p className="text-slate-500 mb-1">Rejection Reason</p>
                  <p className="text-red-700">{viewItem.rejection_reason}</p>
                </div>
              ) : null}
            </div>
          ) : null}
          <DialogFooter className="flex-wrap">
            <Button variant="outline" className="border-slate-200" onClick={() => setViewItem(null)}>Close</Button>
            {canApprove && viewItem && String(viewItem.status || '').toLowerCase() === 'pending' ? (
              <>
                <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => approveRequest(viewItem)} disabled={approving}>
                  <CheckCircle2 size={14} className="mr-1" /> Approve
                </Button>
                <Button variant="outline" className="border-red-200 text-red-600 hover:bg-red-50" onClick={() => { setViewItem(null); openReject(viewItem); }}>
                  <XCircle size={14} className="mr-1" /> Reject
                </Button>
              </>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!rejectItem} onOpenChange={(open) => !open && setRejectItem(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Reject Expense Request</DialogTitle>
            <DialogDescription>Provide a short rejection reason for the requester.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-2 py-2">
            <Label>Rejection Reason</Label>
            <Textarea value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)} placeholder="Explain why the request was rejected" />
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-slate-200" onClick={() => setRejectItem(null)}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={rejectRequest} disabled={rejecting}>
              {rejecting ? 'Rejecting...' : 'Reject Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
